import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Team } from '../store/useStore';
import { X, Mail, ShieldCheck, Loader2 } from 'lucide-react';

interface Props {
  currentTeam: Team;
  updateTeam: (id: string, updates: Partial<Team>) => void;
}

export function EmailVerificationBanner({ currentTeam, updateTeam }: Props) {
  const [dismissed, setDismissed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [emailInput, setEmailInput] = useState(currentTeam.email || '');
  const [otpCode, setOtpCode] = useState('');
  const [step, setStep] = useState<'email' | 'otp' | 'verified'>(currentTeam.email ? 'otp' : 'email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (dismissed) return null;

  const handleSendOtp = async () => {
    if (!emailInput || !emailInput.includes('@')) {
      setError('Veuillez saisir une adresse email valide');
      return;
    }
    setLoading(true);
    setError('');
    const cleanEmail = emailInput.toLowerCase().trim();
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    console.log('[DEBUG OTP] Code de validation pour', cleanEmail, ':', code);
    try {
      // Envoyer l'email OTP via la fonction Edge déjà déployée
      const name = currentTeam?.name || cleanEmail.split('@')[0];
      const { error: sendError } = await supabase.functions.invoke('send-email-verification', {
        body: { email: cleanEmail, code, name }
      });
      if (sendError) {
        console.error('[EmailVerif] Erreur envoi:', sendError.message);
        setError("Impossible d'envoyer le code. Vérifiez votre adresse email.");
        setLoading(false);
        return;
      }
      // Sauvegarder le code en DB (uniquement dans la table team pour éviter le trigger login_otps)
      await supabase.from('staff').update({
        email: cleanEmail,
        verification_code: code,
        verification_code_sent_at: new Date().toISOString()
      }).eq('id', currentTeam.id);
      updateTeam(currentTeam.id, { 
        email: cleanEmail,
        verificationCode: code,
        verificationCodeSentAt: new Date().toISOString()
      });
      setStep('otp');
    } catch (err) {
      console.error('[EmailVerif] Exception:', err);
      setError('Erreur lors de l\'envoi du code');
    }
    setLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length < 6) {
      setError('Veuillez saisir le code à 6 chiffres');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('staff')
        .select('verification_code, verification_code_sent_at')
        .eq('id', currentTeam.id)
        .maybeSingle();

      if (fetchError || !data || data.verification_code !== otpCode) {
        setError('Code invalide ou expiré. Veuillez réessayer.');
        setLoading(false);
        return;
      }

      const sentAt = data.verification_code_sent_at ? new Date(data.verification_code_sent_at).getTime() : 0;
      const isExpired = Date.now() - sentAt > 30 * 60 * 1000; // 30 minutes validity
      if (isExpired) {
        setError('Code expiré. Veuillez en demander un nouveau.');
        setLoading(false);
        return;
      }

      await supabase.from('staff').update({
        email_confirmed: true,
        verification_code: null,
        verification_code_sent_at: null
      }).eq('id', currentTeam.id);
      updateTeam(currentTeam.id, {
        emailConfirmed: true,
        verificationCode: null,
        verificationCodeSentAt: null
      });
      setStep('verified');
      setTimeout(() => { setShowModal(false); setDismissed(true); }, 2000);
    } catch (err) {
      setError('Erreur de vérification');
    }
    setLoading(false);
  };

  return (
    <>
      <div className="bg-orange-500/15 border-b border-orange-500/30 px-4 py-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-orange-300 flex-1">
          <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse shrink-0" />
          <span>Vérifiez votre adresse email pour recevoir les notifications importantes</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setShowModal(true)}
            className="text-[10px] font-bold uppercase tracking-wider text-orange-400 hover:text-orange-300 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 px-3 py-1.5 rounded-lg transition-all whitespace-nowrap"
          >
            {currentTeam.email ? 'Vérifier mon email' : 'Ajouter mon email'}
          </button>
          <button onClick={() => setDismissed(true)} className="text-orange-400/50 hover:text-orange-300 p-1">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Modal de vérification */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-[#14161B] border border-[#2D3139] rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#2D3139]">
              <div className="flex items-center gap-2">
                {step === 'verified' ? (
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                ) : (
                  <Mail className="w-5 h-5 text-orange-400" />
                )}
                <h3 className="font-semibold text-white text-sm">
                  {step === 'email' && 'Ajouter mon email'}
                  {step === 'otp' && 'Vérifier mon email'}
                  {step === 'verified' && 'Email vérifié !'}
                </h3>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-500 hover:text-white p-1">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              {step === 'email' && (
                <>
                  <p className="text-xs text-slate-400">Saisissez votre adresse email pour recevoir les notifications importantes.</p>
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="exemple@email.com"
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-500/50 transition-colors"
                    autoFocus
                  />
                  {error && <p className="text-xs text-red-400">{error}</p>}
                  <button
                    onClick={handleSendOtp}
                    disabled={loading}
                    className="w-full py-2.5 bg-orange-600 hover:bg-orange-500 disabled:opacity-40 text-white font-bold rounded-xl text-sm transition-all flex items-center justify-center gap-2"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Envoyer le code
                  </button>
                </>
              )}

              {step === 'otp' && (
                <>
                  <p className="text-xs text-slate-400">Un code à 6 chiffres vous a été envoyé à <strong className="text-white">{emailInput}</strong></p>
                  <div className="flex justify-center">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="w-32 bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white text-center text-xl font-bold tracking-[8px] focus:outline-none focus:border-indigo-500/50 transition-colors"
                      autoFocus
                    />
                  </div>
                  {error && <p className="text-xs text-red-400 text-center">{error}</p>}
                  <button
                    onClick={handleVerifyOtp}
                    disabled={loading}
                    className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-bold rounded-xl text-sm transition-all flex items-center justify-center gap-2"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Vérifier le code
                  </button>
                  <button
                    onClick={handleSendOtp}
                    disabled={loading}
                    className="w-full text-xs text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    Renvoyer un nouveau code
                  </button>
                </>
              )}

              {step === 'verified' && (
                <div className="flex flex-col items-center gap-3 py-4">
                  <div className="w-14 h-14 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center justify-center">
                    <ShieldCheck className="w-7 h-7 text-emerald-400" />
                  </div>
                  <p className="text-sm text-emerald-300 font-medium">Email vérifié avec succès !</p>
                  <p className="text-xs text-slate-500">Vous recevrez désormais les notifications importantes.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

